import { useCallback, useEffect, useRef } from 'react';

import {
  selectLibrarySyncSettingsState,
  yourLibraryActions,
} from '../store/slices/yourLibrary';
import { useAppDispatch, useAppSelector } from '../store/store';

type WakeLockSentinel = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener?: (type: 'release', listener: () => void) => void;
};

const POLL_INTERVAL_VISIBLE_MS = 2000;
const POLL_INTERVAL_HIDDEN_MS = 6000;
const MAX_POLL_INTERVAL_MS = 12000;

export const useRecordingsAndWorksSyncMonitor = (enabled: boolean = true): void => {
  const dispatch = useAppDispatch();
  const {
    recordingsAndWorksJobId,
    recordingsAndWorksJob,
    isSyncingAlbumRecordingsAndWorks,
  } = useAppSelector(selectLibrarySyncSettingsState);
  const lastCompletedJobIdRef = useRef<string | null>(null);

  const jobStatus = recordingsAndWorksJob?.status;
  const jobId =
    recordingsAndWorksJobId ??
    (jobStatus === 'queued' || jobStatus === 'running' ? recordingsAndWorksJob?.jobId ?? null : null);
  const shouldPoll =
    Boolean(jobId) &&
    (isSyncingAlbumRecordingsAndWorks ||
      jobStatus === 'queued' ||
      jobStatus === 'running' ||
      !jobStatus);

  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const wakeLockRequestedRef = useRef(false);

  const requestWakeLock = useCallback(async () => {
    if (typeof navigator === 'undefined') return;
    const navigatorWithWakeLock = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> };
    };
    if (!navigatorWithWakeLock.wakeLock?.request) return;
    if (wakeLockRef.current) return;

    try {
      const sentinel = await navigatorWithWakeLock.wakeLock.request('screen');
      wakeLockRef.current = sentinel;
      wakeLockRequestedRef.current = true;
      sentinel.addEventListener?.('release', () => {
        wakeLockRef.current = null;
      });
    } catch {
      // best-effort; ignore failures
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (!wakeLockRef.current) return;
    try {
      await wakeLockRef.current.release();
    } catch {
      // best-effort; ignore failures
    } finally {
      wakeLockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      void releaseWakeLock();
      return undefined;
    }

    if (shouldPoll) {
      void requestWakeLock();
      return () => {
        void releaseWakeLock();
      };
    }

    void releaseWakeLock();
    return () => {
      void releaseWakeLock();
    };
  }, [releaseWakeLock, requestWakeLock, shouldPoll, enabled]);

  useEffect(() => {
    if (!enabled || !shouldPoll) return;

    const handleVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (
        document.visibilityState === 'visible' &&
        wakeLockRequestedRef.current &&
        !wakeLockRef.current
      ) {
        void requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [requestWakeLock, shouldPoll, enabled]);

  useEffect(() => {
    if (!enabled || !shouldPoll || !jobId) return undefined;

    let cancelled = false;
    let timeoutId: number | undefined;
    let backoffMs = 0;

    const scheduleNext = (baseDelay: number) => {
      const cappedDelay = Math.min(MAX_POLL_INTERVAL_MS, baseDelay + backoffMs);
      timeoutId = window.setTimeout(tick, cappedDelay);
    };

    const tick = async () => {
      if (cancelled) return;
      const isHidden =
        typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const baseDelay = isHidden ? POLL_INTERVAL_HIDDEN_MS : POLL_INTERVAL_VISIBLE_MS;

      try {
        await dispatch(
          yourLibraryActions.fetchRecordingsAndWorksJobStatus({ jobId })
        ).unwrap();
        backoffMs = 0;
      } catch {
        backoffMs = Math.min(
          MAX_POLL_INTERVAL_MS - baseDelay,
          backoffMs ? Math.max(backoffMs * 1.5, baseDelay) : baseDelay
        );
      }

      if (!cancelled) {
        scheduleNext(baseDelay);
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [dispatch, jobId, shouldPoll, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const completedJobId = recordingsAndWorksJob?.jobId ?? recordingsAndWorksJobId ?? null;
    if (jobStatus === 'succeeded' && completedJobId && lastCompletedJobIdRef.current !== completedJobId) {
      lastCompletedJobIdRef.current = completedJobId;
      void dispatch(yourLibraryActions.hydrateRecordingsAndWorksAfterSync());
    }
  }, [dispatch, enabled, jobStatus, recordingsAndWorksJob?.jobId, recordingsAndWorksJobId]);
};
